import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAssignment } from '../api/assignments';
import * as latexReports from '../api/latexReports';

const LaTeXReportGenerationPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  
  const [assignment, setAssignment] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [reportName, setReportName] = useState('');
  const [storeInGithub, setStoreInGithub] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        // Fetch assignment details
        const assignmentResponse = await getAssignment(token, assignmentId);
        const asgn = assignmentResponse.data;
        setAssignment(asgn);
        setReportName(`${asgn.title} - Marking Report`);
        // Default to class_assigned
        if (asgn.class_assigned) {
          setSelectedClass(asgn.class_assigned.toString());
        }
        
        // Fetch available templates
        const templatesResponse = await latexReports.getTemplates(token);
        const templateList = Array.isArray(templatesResponse.data)
          ? templatesResponse.data
          : (templatesResponse.data.results || []);
        setTemplates(templateList);

        // Select default template if available
        const defaultTemplate = templateList.find(t => t.is_default);
        if (defaultTemplate) {
          setSelectedTemplate(defaultTemplate.id.toString());
        }
        
      } catch (err) {
        setError('Failed to load assignment or templates');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (assignmentId) {
      fetchData();
    }
  }, [assignmentId, token]);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    setError('');
    setGenerationResult(null);

    if (!token) {
      setError('Authentication required');
      setIsGenerating(false);
      return;
    }

    try {
      const reportData = {
        assignment_id: parseInt(assignmentId),
        class_id: selectedClass ? parseInt(selectedClass) : undefined,
        template_id: selectedTemplate ? parseInt(selectedTemplate) : undefined,
        report_name: reportName,
        store_in_github: storeInGithub
      };

      const response = await latexReports.generateReport(token, reportData);
      setGenerationResult(response.data);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate report');
      console.error('Error generating report:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async (reportId) => {
    if (!token) { setError('Authentication required'); return; }
    try {
      const response = await latexReports.downloadPdf(token, reportId);
      const filename = generationResult?.download_filename || 'report.pdf';
      triggerDownload(new Blob([response.data], { type: 'application/pdf' }), filename);
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  const handleDownloadLatex = async (reportId) => {
    if (!token) { setError('Authentication required'); return; }
    try {
      const response = await latexReports.downloadLatex(token, reportId);
      const filename = (generationResult?.download_filename || 'report.pdf').replace('.pdf', '.tex');
      triggerDownload(new Blob([response.data], { type: 'application/x-latex' }), filename);
    } catch (err) {
      setError('Failed to download LaTeX source');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading assignment and templates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex mb-4" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li>
                <Link to="/assignments" className="text-gray-500 hover:text-gray-700">
                  Assignments
                </Link>
              </li>
              <li>
                <span className="mx-2 text-gray-400">/</span>
                <Link 
                  to={`/assignments/${assignmentId}`} 
                  className="text-gray-500 hover:text-gray-700"
                >
                  {assignment?.title}
                </Link>
              </li>
              <li>
                <span className="mx-2 text-gray-400">/</span>
                <span className="text-gray-900">Report Generation</span>
              </li>
            </ol>
          </nav>
          
          <h1 className="text-3xl font-bold text-gray-900">Generate Report</h1>
          {assignment && (
            <p className="mt-2 text-gray-600">
              Create a professional marking report for <strong>{assignment.title}</strong>
            </p>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Generation Form */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Report Configuration</h2>
              
              <form onSubmit={handleGenerateReport} className="space-y-6">
                {/* Report Name */}
                <div>
                  <label htmlFor="reportName" className="block text-sm font-medium text-gray-700">
                    Report Name
                  </label>
                  <input
                    type="text"
                    id="reportName"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Enter report name"
                    required
                  />
                </div>

                {/* Class Selection */}
                {assignment?.assigned_classes_display?.length > 1 && (
                  <div>
                    <label htmlFor="classSelect" className="block text-sm font-medium text-gray-700">
                      Class
                    </label>
                    <select
                      id="classSelect"
                      value={selectedClass}
                      onChange={(e) => setSelectedClass(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                    >
                      <option value="">— Select a class —</option>
                      {assignment.assigned_classes_display.map((cls) => (
                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                      ))}
                    </select>
                    <p className="mt-2 text-sm text-gray-500">
                      Report will include only submissions from the selected class
                    </p>
                  </div>
                )}

                {/* Template Selection */}
                <div>
                  <label htmlFor="template" className="block text-sm font-medium text-gray-700">
                    LaTeX Template
                  </label>
                  <select
                    id="template"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">Use default template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} {template.is_default ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    Select a custom template or use the default comprehensive marking report
                  </p>
                </div>

                {/* GitHub Storage */}
                <div className="flex items-center">
                  <input
                    id="storeInGithub"
                    type="checkbox"
                    checked={storeInGithub}
                    onChange={(e) => setStoreInGithub(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="storeInGithub" className="ml-2 block text-sm text-gray-900">
                    Store report in GitHub repository
                  </label>
                </div>
                <p className="text-sm text-gray-500">
                  Automatically commit both LaTeX source and PDF to your class GitHub repository
                </p>

                {/* Error Display */}
                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={isGenerating}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGenerating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating Report...
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar Info */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Assignment Details</h3>
              {assignment && (
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Title</dt>
                    <dd className="text-sm text-gray-900">{assignment.title}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Class</dt>
                    <dd className="text-sm text-gray-900">{assignment.class_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                    <dd className="text-sm text-gray-900">
                      {assignment.due_date ? new Date(assignment.due_date).toLocaleDateString() : 'No due date'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Max Points</dt>
                    <dd className="text-sm text-gray-900">{assignment.max_points}</dd>
                  </div>
                </dl>
              )}
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">What's included in the report?</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Complete student results table</li>
                <li>• Individual rubric breakdowns</li>
                <li>• Class performance statistics</li>
                <li>• Professional LaTeX formatting</li>
                <li>• GitHub version control</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Generation Result */}
        {generationResult && (
          <div className="mt-8 bg-white shadow rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Report Generated Successfully!</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Report Details</h4>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm text-gray-500">Name:</dt>
                    <dd className="text-sm text-gray-900">{generationResult.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Template:</dt>
                    <dd className="text-sm text-gray-900">{generationResult.template_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Students:</dt>
                    <dd className="text-sm text-gray-900">{generationResult.total_students}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Status:</dt>
                    <dd className="text-sm text-gray-900 capitalize">{generationResult.generation_status.replace('_', ' ')}</dd>
                  </div>
                  {generationResult.generation_time_seconds && (
                    <div>
                      <dt className="text-sm text-gray-500">Generation Time:</dt>
                      <dd className="text-sm text-gray-900">{generationResult.generation_time_seconds.toFixed(2)}s</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Download Options</h4>
                <div className="space-y-2">
                  {generationResult.is_completed && generationResult.actions && (
                    <>
                      <button
                        onClick={() => handleDownloadPdf(generationResult.id)}
                        className="w-full text-left px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md"
                      >
                        📄 Download PDF Report
                      </button>
                      <button
                        onClick={() => handleDownloadLatex(generationResult.id)}
                        className="w-full text-left px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md"
                      >
                        📝 Download LaTeX Source
                      </button>
                    </>
                  )}
                  
                  {generationResult.github_repository_url && (
                    <a
                      href={generationResult.github_repository_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-left px-4 py-2 text-sm text-white bg-gray-800 hover:bg-gray-900 rounded-md"
                    >
                      🔗 View in GitHub
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LaTeXReportGenerationPage;